import React, { useMemo } from 'react'
import { Button } from '@/atoms/Button'
import { ArrowLeftIcon } from '@/atoms/Icons/ArrowLeftIcon'
import { ParagraphMD, TitleLG } from '@/atoms/Text'
import { Accordion, AccordionItem } from '@/molecules/Accordion'
import { useTranslate } from '@/utils/translate/translate-client'
import { GroupTicketsModalTypes } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal/GroupTicketsModal'

interface GroupTicketsFaqProps {
  setModalType: React.Dispatch<React.SetStateAction<GroupTicketsModalTypes>>
  groupTicketsExp: string
}

export const GroupTicketsFaq: React.FC<GroupTicketsFaqProps> = ({ setModalType, groupTicketsExp }) => {
  const { t } = useTranslate('tickets')

  const percentage = useMemo(() => {
    if (groupTicketsExp === '33') return '33'
    if (groupTicketsExp === '20') return '20'
    return '50'
  }, [groupTicketsExp])

  return (
    <>
      <Button
        onClick={() => setModalType('tickets')}
        className="absolute left-6 top-6 cursor-pointer rounded-full bg-core-gray-950 p-1"
        variant="black"
      >
        <ArrowLeftIcon size={18} />
      </Button>

      <TitleLG className="mb-6 mt-10" weight="bold">
        {t('freqAskQuestions', 'Frequently Asked Questions')}
      </TitleLG>

      <div>
        <Accordion>
          <AccordionItem
            contentElementId="collapsible-content-which-theaters-support-this-promotion"
            iconColor="core-gray-950"
            triggerClassName="!border-t-0"
            labelClassName="text-lg text-core-gray-950"
            label={t('whichTheatersSupportGroupTickets', 'Which theaters support group tickets?')}
          >
            <ParagraphMD className="text-left font-medium text-core-gray-700">
              {t(
                'couponCodesAreValidAtAnyTheaterGroupsAngelPartner',
                'Coupon codes are valid at any Angel Partner theater.',
              )}
            </ParagraphMD>
          </AccordionItem>
        </Accordion>
      </div>
      <div className="border-t border-core-gray-300">
        <Accordion>
          <AccordionItem
            contentElementId="collapsible-content-why-are-group-tickets-discounted"
            iconColor="core-gray-950"
            labelClassName="text-lg text-core-gray-950"
            label={t('whatAreGroupTicketsDiscounted', 'Why are group tickets discounted?')}
            triggerClassName="!border-none"
          >
            <ParagraphMD className="text-left font-medium text-core-gray-700">
              {t(
                'theAngelCommunityMatchesGroupTicketsPercentage',
                'We believe great stories are best experienced together. To make that possible, the Angel Community matches every group ticket purchase by {{percentage}}%, helping more families, friends, and communities see our movies on the big screen.',
                {
                  percentage,
                },
              )}
            </ParagraphMD>
          </AccordionItem>
        </Accordion>
      </div>
      <div className="border-y border-core-gray-300">
        <Accordion>
          <AccordionItem
            contentElementId="collapsible-content-how-do-group-tickets-work"
            iconColor="core-gray-950"
            labelClassName="text-lg text-core-gray-950"
            label={t('howDoGroupTicketsWork', 'How do group tickets work?')}
            triggerClassName="!border-none"
          >
            <ParagraphMD className="text-left font-medium text-core-gray-700">
              {t(
                'youPayTheDiscountedPriceUpfrontPercentage',
                'You pay the discounted price upfront, with the {{percentage}}% Angel Community match already applied. After purchase, you’ll get a coupon code for your group’s tickets. You can share the code so everyone picks their own seats, or use it yourself to reserve all the seats together.',
                {
                  percentage,
                },
              )}
            </ParagraphMD>
          </AccordionItem>
        </Accordion>
      </div>

      <Button
        onClick={() => setModalType('tickets')}
        className="mx-auto mt-6 flex w-fit items-center justify-center px-16"
        variant="lightgray"
      >
        {t('goBack', 'Go Back')}
      </Button>
    </>
  )
}
